import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TopNav } from "@/components/TopNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Spinner, FloppyDisk, User, Buildings } from "@phosphor-icons/react";

export default function Settings() {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [business, setBusiness] = useState(user?.business_name || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setName(user.name || "");
      setBusiness(user.business_name || "");
    }
  }, [user]);

  const dirty = name.trim() !== (user?.name || "") || business.trim() !== (user?.business_name || "");

  const save = async () => {
    if (!name.trim() || !business.trim()) return toast.error("Name and business name are required");
    setSaving(true);
    try {
      const { data } = await api.put("/auth/me", { name: name.trim(), business_name: business.trim() });
      updateUser(data);
      toast.success("Account updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen camino-dark">
      <TopNav>
        <Button variant="ghost" onClick={() => navigate("/")} className="gap-2 text-slate-500 px-2 sm:px-3" data-testid="settings-back-btn">
          <ArrowLeft size={16} /> <span className="hidden sm:inline">Dashboard</span>
        </Button>
      </TopNav>

      <main className="mx-auto max-w-xl px-4 sm:px-6 py-10">
        <div className="animate-fade-up">
          <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-action">Account</p>
          <h1 className="mt-2 font-heading text-4xl font-bold tracking-tight text-navy leading-none">Settings</h1>
          <p className="mt-3 text-slate-500">Update your name and business name. These appear on the documents you send.</p>
        </div>

        <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 space-y-5 animate-fade-up">
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Email</Label>
            <div className="h-11 flex items-center rounded-md border border-slate-200 bg-slate-50 px-3 text-sm text-slate-500" data-testid="settings-email">
              {user?.email}
            </div>
            <p className="text-[11px] text-slate-400">Your login email can't be changed here.</p>
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Your name</Label>
            <div className="relative">
              <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={name} onChange={(e) => setName(e.target.value)} className="h-11 pl-10" data-testid="settings-name-input" />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Business name</Label>
            <div className="relative">
              <Buildings size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={business} onChange={(e) => setBusiness(e.target.value)} className="h-11 pl-10" data-testid="settings-business-input" />
            </div>
          </div>

          <Button onClick={save} disabled={saving || !dirty} className="w-full h-11 rounded-full bg-navy hover:bg-navy/90 gap-2" data-testid="settings-save-btn">
            {saving ? <><Spinner size={18} className="animate-spin" /> Saving…</> : <><FloppyDisk size={18} weight="bold" /> Save changes</>}
          </Button>
        </div>
      </main>
    </div>
  );
}
